import { useRef, useMemo, useCallback, useEffect } from 'react';
import { FormAction, TableCellValue, TableHeader } from '../../../../../../../models/SidePanelTypes';
import ButtonAsync from '../../../../../../button/ButtonAsync';
import { isArray, isNumber, isString } from '../../../../../../../helpers/typeHelpers';
import { useTableAction } from './useTableAction';

const getRadioValueHolder = (wrapper: Element) => wrapper.querySelector<HTMLInputElement>('input[type="hidden"], input[type="text"]');

export const TABLE_FORM_ACTION_ERROR_ID = 'table-form-action-error';


export const useTableForm = (chatId?: string, headers?: TableHeader[], rows?: TableCellValue[][], actions?: FormAction[]) => {
    const tableFormRef = useRef<HTMLFormElement>(null);
    const tableFormData = useMemo<Record<string, string>>(() => ({}), []);

    useEffect(() => {
        rows?.forEach((row, rowIndex) => {
            row.forEach((col, colIndex) => {
                const header = headers?.[colIndex];
                if (!header?.form?.name)
                    return;
                if (!isArray(col)) {
                    if (isString(col) || isNumber(col) || "value" in col)
                        col = [col];
                    else
                        return;
                }
                const colVal = col[0];
                if (colVal === undefined) {
                    return;
                }
                if (!isString(colVal) && !isNumber(colVal) && "value" in colVal) {
                    tableFormData[`${rowIndex}-${header.form!.name}`] = colVal.value.toString();
                };
            });
        });
    }, [headers, rows, tableFormData]);

    const handleTableFormInput = useCallback((event?: React.FormEvent<HTMLFormElement> | HTMLFormElement) => {
        const form = tableFormRef.current;
        if (!form) {
            return;
        }
        const eventFormElem = event?.target as HTMLInputElement | undefined;
        const eventFormElmIsRadio = eventFormElem?.type === "radio";
        const values: Record<string, string | null> = {};
        form.querySelectorAll('div.form').forEach(wrapper => {
            let valueHolder = getRadioValueHolder(wrapper);

            if (valueHolder) {
                if (eventFormElmIsRadio && wrapper.contains(eventFormElem)) {
                    valueHolder.value = eventFormElem.value;
                }

                const radios = wrapper.querySelectorAll<HTMLInputElement>('input[type="radio"]');
                radios.forEach(radio => radio.checked = radio.value === valueHolder!.value);
            }

            valueHolder ??= wrapper.querySelector('input[name]');
            if (!valueHolder || !valueHolder.name) {
                return;
            }
            let value = valueHolder.type !== "checkbox" || valueHolder.checked ? valueHolder.value : "";
            values[valueHolder.name] = value;
        });


        Object.entries(values).forEach(([key, value]) => {
            if (value === null)
                delete tableFormData[key];
            else
                tableFormData[key] = value;
        });

    }, [tableFormData]);

    const handleTablePageChange = useCallback(() => {

        const form = tableFormRef.current;
        if (!form) {
            return;
        }
        form.querySelectorAll<HTMLInputElement>('input').forEach(input => {
            if (!input.name) {
                return;
            }

            if (input.name.startsWith('__ui-')) {
                return;
            }

            const formValue = tableFormData[input.name];
            if (formValue === undefined) {
                return;
            }


            if (input.type === "checkbox") {
                input.checked = input.value === formValue.toString();
            }
            else if (input.type === "text" || input.type === "hidden") {
                input.value = formValue.toString() || "";
            }
        });
        handleTableFormInput();
    }, [handleTableFormInput, tableFormData]);

    const { mutateAsync } = useTableAction();
    const handleTableFormAction = useCallback(async (action: FormAction) => {
        if (!chatId) return;
        await mutateAsync({ sessionId: chatId, formAction: action, data: tableFormData });
    }, [tableFormData, chatId, mutateAsync]);
    const tableFormActions = useMemo(() => {
        if (!actions) return null;
        return actions.map((action, i) => {
            return <ButtonAsync key={i} label={action.label} onClick={() => handleTableFormAction(action)} />;
        });
    }, [actions, handleTableFormAction]);

    return {
        tableFormData, tableFormRef, handleTableFormInput, handleTablePageChange, tableFormActions
    };
};